import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

const CPAAS_CLIENT_ID = 'client-32704bca-981d-4e79-b957-9e0d9ff0fc4b';
const CPAAS_CLIENT_SECRET = 'PgdpahN0765EJYyXsdRj3l5wX1FnsWrJ';
const TOKEN_URL = '/cpaas-telekom/auth/realms/wsel/protocol/openid-connect/token';

const SK_ACCESS_TOKEN       = 'cpaas_access_token';
const SK_REFRESH_TOKEN      = 'cpaas_refresh_token';
const SK_EXPIRES_AT         = 'cpaas_expires_at';
const SK_REFRESH_EXPIRES_AT = 'cpaas_refresh_expires_at';

@Injectable({ providedIn: 'root' })
export class CpaasAuthService {
  private http = inject(HttpClient);

  accessToken: string | null = null;
  private refreshToken: string | null = null;
  private tokenExpiresAt: number | null = null;
  private refreshExpiresAt: number | null = null;

  constructor() {
    this.restoreSession();
  }

  // ── Public state ──────────────────────────────────────────────────────────

  isTokenExpired(): boolean {
    return !this.tokenExpiresAt || Date.now() >= this.tokenExpiresAt;
  }

  isRefreshTokenExpired(): boolean {
    return !this.refreshExpiresAt || Date.now() >= this.refreshExpiresAt;
  }

  get tokenStatusClass(): string {
    if (!this.accessToken) return 'token-error';
    if (this.isTokenExpired()) return 'token-warn';
    return 'token-ok';
  }

  get tokenStatusLabel(): string {
    if (!this.accessToken) return 'Sin sesión';
    if (this.isTokenExpired()) {
      return this.isRefreshTokenExpired()
        ? 'Sesión expirada — necesitas hacer login de nuevo'
        : 'Access token expirado (refresh disponible)';
    }
    const remaining = Math.round((this.tokenExpiresAt! - Date.now()) / 1000);
    return `Token activo — expira en ${remaining}s`;
  }

  // ── Auth operations ───────────────────────────────────────────────────────

  async login(username: string, password: string, otp?: string): Promise<void> {
    let params = new HttpParams()
      .set('client_id', CPAAS_CLIENT_ID)
      .set('client_secret', CPAAS_CLIENT_SECRET)
      .set('username', username)
      .set('password', password)
      .set('grant_type', 'password');

    if (otp) params = params.set('otp', otp);

    const response = await firstValueFrom(
      this.http.post<any>(TOKEN_URL, params.toString(), {
        headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' })
      })
    );
    this.storeTokens(response);
  }

  async refreshAccessToken(): Promise<void> {
    if (this.isRefreshTokenExpired()) {
      throw new Error('Refresh token expirado. Haz login de nuevo.');
    }
    const params = new HttpParams()
      .set('client_id', CPAAS_CLIENT_ID)
      .set('client_secret', CPAAS_CLIENT_SECRET)
      .set('refresh_token', this.refreshToken!)
      .set('grant_type', 'refresh_token');

    const response = await firstValueFrom(
      this.http.post<any>(TOKEN_URL, params.toString(), {
        headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' })
      })
    );
    this.storeTokens(response);
  }

  async ensureValidToken(): Promise<boolean> {
    if (!this.accessToken) return false;
    if (this.isTokenExpired()) {
      try {
        await this.refreshAccessToken();
      } catch {
        return false;
      }
    }
    return !this.isTokenExpired();
  }

  clearSession(): void {
    this.accessToken = null;
    this.refreshToken = null;
    this.tokenExpiresAt = null;
    this.refreshExpiresAt = null;
    sessionStorage.removeItem(SK_ACCESS_TOKEN);
    sessionStorage.removeItem(SK_REFRESH_TOKEN);
    sessionStorage.removeItem(SK_EXPIRES_AT);
    sessionStorage.removeItem(SK_REFRESH_EXPIRES_AT);
  }

  // ── Session persistence ───────────────────────────────────────────────────

  private storeTokens(response: any): void {
    this.accessToken = response.access_token;
    this.refreshToken = response.refresh_token;
    this.tokenExpiresAt = Date.now() + (response.expires_in - 5) * 1000;
    this.refreshExpiresAt = Date.now() + (response.refresh_expires_in - 10) * 1000;

    sessionStorage.setItem(SK_ACCESS_TOKEN, this.accessToken!);
    sessionStorage.setItem(SK_REFRESH_TOKEN, this.refreshToken!);
    sessionStorage.setItem(SK_EXPIRES_AT, this.tokenExpiresAt.toString());
    sessionStorage.setItem(SK_REFRESH_EXPIRES_AT, this.refreshExpiresAt.toString());
  }

  private restoreSession(): void {
    const accessToken       = sessionStorage.getItem(SK_ACCESS_TOKEN);
    const refreshToken      = sessionStorage.getItem(SK_REFRESH_TOKEN);
    const expiresAt         = sessionStorage.getItem(SK_EXPIRES_AT);
    const refreshExpiresAt  = sessionStorage.getItem(SK_REFRESH_EXPIRES_AT);

    if (!accessToken || !refreshToken || !expiresAt || !refreshExpiresAt) return;

    this.accessToken       = accessToken;
    this.refreshToken      = refreshToken;
    this.tokenExpiresAt    = parseInt(expiresAt, 10);
    this.refreshExpiresAt  = parseInt(refreshExpiresAt, 10);
  }
}
